/**
 * The listing foot every long list in the console wears: how many rows a page holds, and the way
 * through them.
 *
 * ## Why it is one module and not three
 *
 * The founder asked for the same thing on three screens on 2026-09-16 — "Documents, Envelopes …
 * per page should display 10 only, and also give open to show per page as 10, 15, 20, 30, 50, 100.
 * the default is 10", and the same sentence again for Contacts. Three copies of a list of six
 * numbers is three chances for one screen to offer 30 and another not to, which is exactly what
 * had happened: the library shipped `[10, 15, 20, 50, 100]` while the ask said 30, and the other
 * two screens paginated not at all. So the numbers, the default, the URL parameter's spelling and
 * the bar itself all live here, and a screen that wants pagination imports it rather than
 * describing it again.
 *
 * ## 100 is the ceiling because the API's is
 *
 * `GET /v1/envelopes` answers 422 `invalid_limit` above 100, so a larger option would be a control
 * whose only outcome is an error message. Contacts is read whole and paged in the browser, and
 * takes the same ceiling anyway: a list of six numbers that means something different on one
 * screen is worse than a ceiling nobody reaches.
 *
 * ## No "page 3 of 12" unless the caller can prove it
 *
 * Two of the three callers page by CURSOR — the cursor is the sort key, opaque, never an offset —
 * and the API returns no count, so a total is not a number they have. `pageCount` is therefore
 * optional: Contacts holds the whole book and passes it, the two cursor-paged lists do not, and
 * the bar says what it knows rather than a figure that stops being true the moment you filter.
 */
import { Button } from '@flowza/ui'

/** The six page sizes, in the founder's order. */
export const PER_PAGE_OPTIONS = [10, 15, 20, 30, 50, 100] as const
export const DEFAULT_PER_PAGE = 10

/** The search parameter the page size travels in, so three screens spell it the same way. */
export const PER_PAGE_PARAM = 'per'

/**
 * The `per` search parameter, or the default.
 *
 * An unknown, unparseable or out-of-range value is NOT honoured — it falls back to 10 rather than
 * being clamped, because `?per=1000` is either a typo or somebody probing, and the honest answer
 * to both is the default. Importantly this also means the value handed to the API is always one of
 * six known-good numbers, so the 422 above can never be reached from this control.
 */
export function perPageFrom(search: URLSearchParams): number {
  const raw = Number(search.get(PER_PAGE_PARAM))
  return PER_PAGE_OPTIONS.find((n) => n === raw) ?? DEFAULT_PER_PAGE
}

export function Pagination({
  perPage,
  page,
  pageCount,
  rowsOnPage,
  hasNext,
  hasPrevious,
  noun,
  onPerPageChange,
  onNext,
  onPrevious,
}: {
  readonly perPage: number
  /** 1-based. For a cursor-paged list it is the depth of the cursor stack plus one. */
  readonly page: number
  /** How many pages there are, when the caller actually knows. Omitted, no total is claimed. */
  readonly pageCount?: number
  readonly rowsOnPage: number
  readonly hasNext: boolean
  readonly hasPrevious: boolean
  /** The singular word for a row: `document`, `envelope`, `contact`. Pluralised with an `s`. */
  readonly noun: string
  readonly onPerPageChange: (n: number) => void
  readonly onNext: () => void
  readonly onPrevious: () => void
}) {
  /*
    Hidden only when it could do nothing. One page AND few enough rows that no page size would
    split them — because hiding the bar on a single page hides the SIZE control with it, and
    somebody who has just set 100 would have no way back to 10 short of editing the URL. So the
    test is not "is there a second page" but "could this listing ever have one": more rows than
    the smallest option means dropping to it would paginate, and the control earns its place.
  */
  if (!hasNext && !hasPrevious && rowsOnPage <= PER_PAGE_OPTIONS[0]) return null
  return (
    <nav
      aria-label="Pages"
      className="border-border-subtle flex flex-wrap items-center justify-between gap-3 border-t pt-3"
    >
      <label className="text-text-muted flex items-center gap-2 text-sm">
        Rows per page
        <select
          className="h-control border-border-strong bg-field-bg text-text-primary rounded-sm border px-2 text-sm"
          value={perPage}
          onChange={(e) => onPerPageChange(Number(e.currentTarget.value))}
        >
          {PER_PAGE_OPTIONS.map((n) => (
            <option key={n} value={n}>
              {n}
            </option>
          ))}
        </select>
      </label>
      <div className="flex items-center gap-3">
        <p aria-live="polite" className="text-text-muted m-0 text-sm">
          Page {page}
          {pageCount === undefined ? '' : ` of ${pageCount}`} · {rowsOnPage} {noun}
          {rowsOnPage === 1 ? '' : 's'}
        </p>
        <Button variant="secondary" disabled={!hasPrevious} onClick={onPrevious}>
          Previous
        </Button>
        <Button variant="secondary" disabled={!hasNext} onClick={onNext}>
          Next
        </Button>
      </div>
    </nav>
  )
}

export default Pagination
